import { Navigate, Outlet, useLocation, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { LogoLoading } from "@/components/logo-loading";
import { Button } from "@/components/ui/button";
import { isOnboarded, useCurrentUser } from "@/lib/auth/use-current-user";
import { activeImpersonation } from "@/lib/impersonation/client";
import { NotFound } from "@/src/routes/not-found";
import { useAuth } from "./auth-provider";

/**
 * Where to send a freshly authenticated user. An invitation in the URL wins;
 * otherwise back to the page that bounced them to `/sign-in` (same-origin
 * path only, never an auth page); otherwise the workspace.
 */
export function postAuthDestination(
  searchParams: URLSearchParams,
  state?: unknown,
): string {
  const invitationId = searchParams.get("invitation_id");
  if (invitationId) {
    return `/onboarding?invitation_id=${encodeURIComponent(invitationId)}`;
  }
  const from = (state as { from?: unknown } | null | undefined)?.from;
  if (
    typeof from === "string" &&
    from.startsWith("/") &&
    !from.startsWith("//") &&
    !from.startsWith("/sign-") &&
    !from.startsWith("/auth/")
  ) {
    return from;
  }
  return "/workspace";
}

export function FullScreenLoader() {
  return (
    <div className="flex h-screen">
      <LogoLoading />
    </div>
  );
}

/**
 * What the very first paint shows while the session and the user row are
 * checked. Same logo as the tabs' wait right after it: the load reads as one
 * breath rather than two screens in a row.
 */
export function DashboardSkeleton() {
  return (
    <div className="flex h-screen">
      <div className="w-60 shrink-0 border-r bg-muted/30" />
      <div className="flex flex-1 min-w-0 flex-col">
        <LogoLoading />
      </div>
    </div>
  );
}

/** Anything that needs a Supabase session. */
export function RequireAuth() {
  const { session, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullScreenLoader />;
  if (!session) {
    return (
      <Navigate
        to="/sign-in"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }
  return <Outlet />;
}

/**
 * Auth pages. The forms no longer navigate on success: the session showing up
 * is what moves the user on, which also covers OTP, password and OAuth alike.
 */
export function RedirectIfAuthed() {
  const { session, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center flex-col justify-center gap-4" />
    );
  }
  if (session) {
    return (
      <Navigate
        to={postAuthDestination(searchParams, location.state)}
        replace
      />
    );
  }
  return <Outlet />;
}

/** The dashboard gate: a `users` row with name, position and organisation. */
export function RequireOnboarded() {
  const { t } = useTranslation("common");
  const { data: user, isLoading, error, mutate } = useCurrentUser();

  if (isLoading) return <DashboardSkeleton />;
  if (error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">
          {t("PROFILE_LOAD_FAILED")}
        </p>
        <Button variant="outline" size="sm" onClick={() => void mutate()}>
          {t("RETRY")}
        </Button>
      </div>
    );
  }
  if (!isOnboarded(user)) return <Navigate to="/onboarding" replace />;
  return <Outlet />;
}

/**
 * The platform console. A non-admin gets a 404, not a redirect: the route has
 * no reason to announce itself. Identities cannot be chained: while an admin
 * is inside a client's session, the console is out of reach.
 */
export function RequirePlatformAdmin() {
  const { data: user, isLoading } = useCurrentUser();

  if (isLoading || !user) return <LogoLoading />;
  if (activeImpersonation(user.id)) return <Navigate to="/workspace" replace />;
  if (!user.is_platform_admin) return <NotFound />;
  return <Outlet />;
}
